import type {
  AmhsLiveInventoryGate,
  LiveFoupKind,
  LiveGateRuntimeSnapshot,
  LiveOhtRuntimeFrame,
  LiveOhtRuntimeSnapshot,
  OhtJunctionWaitTimelineEvent,
  OhtTimelineEvent,
  OhtTransferTimelineEvent,
} from '../../types/amhsStream';
import type {
  BrowserKineticIdleVehicle,
  BrowserKineticVehicleLeg,
} from '../../types/simulationPlayback';
import { getPlaybackPosition, legHeadingDeg } from '../../utils/simulationPlayback';
import { FoupOwnershipLedger } from '../../utils/replay/foupOwnershipLedger';
import {
  gateOwnershipCommitProgress,
  resolveGateFoupState,
  resolveGateShutterOpen,
  resolveGateTransferContext,
  resolveGateTransferPhase,
  validateGateTransferStart,
  type GateTransferContext,
} from './gateTransfer';
import type { DerivedGateModule } from '../../utils/derivedGateEquipment';
import { resolvePickupFoupKind } from './foupKind';
import {
  STOCKER_DROP_LP_RELEASE_PROGRESS,
  STOCKER_PICKUP_OHT_TAKE_PROGRESS,
} from '../../components/canvas/amhs/Stocker/stockerTransferTiming';
import { transferAnimationPipelineStore } from '../../store/transferAnimationPipelineStore';
import { resolveTransferStationId } from './transferStationId';
import { snapPoseToRail, type LiveRailIndex, type RailTravel } from './liveRailPose';

type ActiveWallHold = {
  event: OhtTransferTimelineEvent;
  elapsedWallS: number;
  elapsedSimS: number;
  durationSimS: number;
  anchorX: number;
  anchorY: number;
  anchorAngle: number;
  gateContext: GateTransferContext | null;
  ownershipCommitted: boolean;
  /** Stocker crane/lifter still running after the OHT sim hold elapsed. */
  awaitingCrane: boolean;
};

type ActiveSimWait = {
  event: OhtJunctionWaitTimelineEvent;
  releaseGlobalS: number;
  anchorLocalS: number;
  anchorX: number;
  anchorY: number;
  anchorAngle: number;
};

type MutableOhtState = {
  offsetS: number;
  lastGlobalS: number | null;
  /**
   * Heading of the last leg this OHT actually travelled.
   *
   * Dwell legs (pickup/drop holds, junction waits) are zero-length, so their
   * atan2 heading is undefined. Feeding this back as the idle angle keeps the
   * vehicle facing along the rail through a transfer instead of snapping to 0.
   */
  lastHeadingDeg: number;
  /** Rail of the last leg travelled; stationary poses are seated on it. */
  lastRail: RailTravel | null;
  carriedFoupId: string | null;
  /** Resolved at each pickup; only a seek clears it. */
  carriedFoupKind: LiveFoupKind | null;
  completedTransfers: Set<string>;
  completedSimWaits: Set<string>;
  skippedLateTransfers: Set<string>;
  activeWallHold: ActiveWallHold | null;
  activeSimWait: ActiveSimWait | null;
  lastSnapshot: LiveOhtRuntimeSnapshot | null;
  invalidGateTransfer: LiveGateRuntimeSnapshot | null;
  reportedInvalidTransfers: Set<string>;
};

type PreparedOhtTimeline = {
  legs: BrowserKineticVehicleLeg[];
  waits: OhtJunctionWaitTimelineEvent[];
  transfers: OhtTransferTimelineEvent[];
  playback: { ohtId: string; legs: BrowserKineticVehicleLeg[] };
  idle: BrowserKineticIdleVehicle;
};

const EPSILON_S = 1e-6;
const PRESENCE_RADIUS = 2;

const isMovingLeg = (leg: BrowserKineticVehicleLeg | null): boolean =>
  !!leg && (leg.fromX !== leg.toX || leg.fromY !== leg.toY);

const normalizeDeg = (deg: number): number => {
  const n = ((deg % 360) + 360) % 360;
  return n > 360 - 1e-9 ? 0 : n;
};

/** Shortest signed turn from `fromDeg` to `toDeg`, in (-180, 180]. */
const signedDeltaDeg = (fromDeg: number, toDeg: number): number => {
  const d = normalizeDeg(toDeg - fromDeg);
  return d > 180 ? d - 360 : d;
};

/**
 * Vehicle handover length on the DES clock. Pickup/drop seconds are already
 * reserved in the streamed legs (`vehicleFreeSimTimeS`, or the next travel
 * hop). Playing that window keeps every OHT on one timeline; a wall-clock
 * freeze used to shove only the busy vehicle behind everyone else.
 */
function handoverDurationSimS(
  event: OhtTransferTimelineEvent,
  legs: readonly BrowserKineticVehicleLeg[]
): number {
  const free = event.frame.vehicleFreeSimTimeS;
  if (typeof free === 'number' && Number.isFinite(free) && free >= event.startS) {
    return Math.max(0, free - event.startS);
  }
  for (const leg of legs) {
    if (leg.startS <= event.startS + EPSILON_S) continue;
    if (leg.startS >= event.endS - EPSILON_S) break;
    if (isMovingLeg(leg)) return Math.max(0, leg.startS - event.startS);
  }
  if (event.holdSimSec > 0) return event.holdSimSec;
  return Math.max(0, event.endS - event.startS);
}

function holdProgress01(hold: ActiveWallHold): number {
  return hold.durationSimS > 0 ? Math.min(1, hold.elapsedSimS / hold.durationSimS) : 1;
}

/**
 * Stocker inventory is a post-animation state. Unlike a tool/gate handoff,
 * where ownership changes at the physical grip/release beat, a stocker count
 * must stay unchanged until the complete crane/hoist choreography has landed.
 */
function isStockerTransfer(event: OhtTransferTimelineEvent): boolean {
  if (event.frame.stockerId?.trim()) return true;
  return /^(?:ST|STOCKER)[-_]/i.test(event.frame.portId.trim());
}

/** Stocker OHT grip/release beats — inventory still waits for crane via pipeline. */
function stockerOwnershipCommitProgress(
  event: OhtTransferTimelineEvent
): number | null {
  if (!isStockerTransfer(event)) return null;
  return event.action === 'PICKUP'
    ? STOCKER_PICKUP_OHT_TAKE_PROGRESS
    : STOCKER_DROP_LP_RELEASE_PROGRESS;
}

function transferStationId(event: OhtTransferTimelineEvent): string {
  return resolveTransferStationId(event.frame);
}

function tryArmWallHold(
  state: MutableOhtState,
  due: OhtTransferTimelineEvent,
  hold: Omit<ActiveWallHold, 'event' | 'awaitingCrane'>
): boolean {
  const stationId = transferStationId(due);
  if (stationId && !transferAnimationPipelineStore.canArmTransfer(stationId, due.eventId)) {
    return false;
  }
  if (stationId) {
    transferAnimationPipelineStore.markArmed(stationId, due.eventId, {
      kind: isStockerTransfer(due) ? 'stocker' : 'equipment',
      portId: due.frame.portId,
    });
  }
  state.activeWallHold = { event: due, ...hold, awaitingCrane: false };
  return true;
}

function skipPipelineTransfer(event: OhtTransferTimelineEvent): void {
  const stationId = transferStationId(event);
  if (stationId) transferAnimationPipelineStore.skipEvent(stationId, event.eventId);
}

function syncCranePhaseIfReady(
  stationId: string,
  eventId: string,
  holdProgress: number
): void {
  if (!stationId || !eventId || holdProgress + EPSILON_S < 1) return;
  if (!transferAnimationPipelineStore.isAwaitingCrane(stationId, eventId)) return;
  transferAnimationPipelineStore.markCranePhaseComplete(stationId, eventId);
}

function idleFor(
  ohtId: string,
  legs: readonly BrowserKineticVehicleLeg[]
): BrowserKineticIdleVehicle {
  const first = legs[0];
  return {
    vehicleId: ohtId,
    x: first?.fromX ?? 0,
    y: first?.fromY ?? 0,
    homeX: first?.fromX ?? 0,
    homeY: first?.fromY ?? 0,
  };
}

function currentNodeAt(legs: readonly BrowserKineticVehicleLeg[], localS: number): string | null {
  let node: string | null = legs[0]?.fromId ?? null;
  for (const leg of legs) {
    if (leg.startS > localS) break;
    node = localS < leg.endS ? leg.fromId : leg.toId;
  }
  return node;
}

function transferPresence(
  event: OhtTransferTimelineEvent,
  snapshot: Pick<LiveOhtRuntimeSnapshot, 'x' | 'y' | 'currentNode' | 'state'>
): boolean {
  const { frame } = event;
  if (Number.isFinite(frame.x) && Number.isFinite(frame.y)) {
    return Math.hypot(snapshot.x - frame.x!, snapshot.y - frame.y!) <= PRESENCE_RADIUS;
  }
  if (frame.nodeId) return snapshot.currentNode === frame.nodeId;
  // Older stocker/gate events name only a port. They are considered present
  // only while the kinetic schedule is already parked at the handoff time.
  return snapshot.state !== 'MOVING';
}

function waitAt(
  events: readonly OhtTimelineEvent[],
  localS: number
): OhtJunctionWaitTimelineEvent | null {
  return (
    events.find(
      (event): event is OhtJunctionWaitTimelineEvent =>
        event.type === 'junction_wait' &&
        event.startS <= localS + EPSILON_S &&
        localS < event.endS - EPSILON_S
    ) ?? null
  );
}

function gateSnapshot(
  event: OhtTransferTimelineEvent,
  context: GateTransferContext,
  progress01: number,
  validation: { ok: boolean; reason?: string },
  status: LiveGateRuntimeSnapshot['status'],
  x: number,
  y: number
): LiveGateRuntimeSnapshot {
  return {
    equipmentId: context.equipmentId,
    role: context.role,
    portId: event.frame.portId,
    nodeId: context.nodeId,
    x,
    y,
    status,
    activeEventId: event.eventId,
    activeOhtId: event.ohtId,
    foupId: event.frame.foupId || null,
    progress01,
    phase: validation.ok ? resolveGateTransferPhase(progress01, event.action) : null,
    foupState: validation.ok
      ? resolveGateFoupState(progress01, event.action)
      : context.role === 'entry'
        ? 'IN_ENTRY_EQUIPMENT'
        : 'ON_OHT',
    shutterOpen01: validation.ok ? resolveGateShutterOpen(progress01, event.action) : 0,
    validationOk: validation.ok,
    validationReason: validation.reason,
  };
}

export class LiveOhtScheduler {
  private preparedTimelines = new Map<string, PreparedOhtTimeline>();
  private orderedOhtIds: string[] = [];
  private states = new Map<string, MutableOhtState>();
  private ledger = new FoupOwnershipLedger();
  private gateInventory: AmhsLiveInventoryGate[] = [];
  private gateModules: DerivedGateModule[] = [];
  private resolvedGateSourcesCache: AmhsLiveInventoryGate[] | null = null;
  private railIndex: LiveRailIndex | null = null;
  private railTravelCache = new WeakMap<BrowserKineticVehicleLeg, RailTravel | null>();
  /**
   * `getState()` deep-clones the whole ledger, so calling it every frame was
   * copying every FOUP and port array 60x per second. Ownership only changes on
   * a transfer commit, so the derived view is rebuilt on ledger version change.
   */
  private ledgerCache: {
    ledger: FoupOwnershipLedger;
    version: number;
    ownership: Record<string, string | null>;
    portFoupIds: Record<string, string[]>;
  } | null = null;
  private frame: LiveOhtRuntimeFrame = {
    globalSimTimeS: 0,
    byOhtId: {},
    gateByPortId: {},
    portFoupIds: {},
  };

  setTimelines(timelines: Record<string, OhtTimelineEvent[]>): void {
    this.preparedTimelines.clear();
    this.orderedOhtIds = Object.keys(timelines).sort((a, b) => a.localeCompare(b));
    for (const ohtId of this.orderedOhtIds) {
      const events = timelines[ohtId] ?? [];
      const legs: BrowserKineticVehicleLeg[] = [];
      const waits: OhtJunctionWaitTimelineEvent[] = [];
      const transfers: OhtTransferTimelineEvent[] = [];
      for (const event of events) {
        if (event.type === 'vehicle_leg') legs.push(event.leg);
        else if (event.type === 'junction_wait') waits.push(event);
        else if (event.type === 'transfer_event') transfers.push(event);
      }
      this.preparedTimelines.set(ohtId, {
        legs,
        waits,
        transfers,
        playback: { ohtId, legs },
        idle: idleFor(ohtId, legs),
      });
      this.ensureState(ohtId);
    }
  }

  setGateInventory(gates: readonly AmhsLiveInventoryGate[]): void {
    this.gateInventory = gates.map((gate) => ({ ...gate }));
    this.resolvedGateSourcesCache = null;
  }

  setGateModules(modules: readonly DerivedGateModule[]): void {
    this.gateModules = modules.map((module) => ({ ...module }));
    this.resolvedGateSourcesCache = null;
  }

  /**
   * Drawn rails used to seat rendered vehicles on the track centerline.
   * Streamed coordinates still drive timing and presence checks.
   */
  setRailIndex(index: LiveRailIndex | null): void {
    const next = index && !index.isEmpty ? index : null;
    if (next === this.railIndex) return;
    this.railIndex = next;
    this.railTravelCache = new WeakMap();
    for (const state of this.states.values()) state.lastRail = null;
  }

  private railTravelFor(leg: BrowserKineticVehicleLeg): RailTravel | null {
    if (!this.railIndex) return null;
    let travel = this.railTravelCache.get(leg);
    if (travel === undefined) {
      travel = this.railIndex.travelForLeg(leg);
      this.railTravelCache.set(leg, travel);
    }
    return travel;
  }

  /**
   * Moves a streamed pose onto the drawn rail centerline and faces it along the
   * rail. While moving, the corner blend's offset from the leg chord is kept so
   * turns still ease instead of snapping. A parked vehicle (transfer, dwell,
   * wait) is seated on whichever rail matches the heading it stopped with, so
   * a hold rewound onto the next leg cannot swing it onto the departure rail.
   */
  private railSeatedPose(
    state: MutableOhtState,
    x: number,
    y: number,
    angle: number,
    movingLeg: BrowserKineticVehicleLeg | null
  ): { x: number; y: number; angle: number } {
    if (!this.railIndex) return { x, y, angle };
    const chordDeg = movingLeg ? legHeadingDeg(movingLeg, angle) : angle;
    const blendDeg = movingLeg ? signedDeltaDeg(chordDeg, angle) : 0;
    const onLegRail = movingLeg && state.lastRail ? snapPoseToRail(state.lastRail, x, y) : null;
    const seated = onLegRail ?? this.railIndex.nearestAlignedPose(x, y, chordDeg);
    if (!seated) return { x, y, angle };
    return { x: seated.x, y: seated.y, angle: normalizeDeg(seated.angle + blendDeg) };
  }

  /** Ownership + port occupancy for this tick, cloned only when it changed. */
  private ledgerView(): {
    ownership: Record<string, string | null>;
    portFoupIds: Record<string, string[]>;
  } {
    const version = this.ledger.getVersion();
    const cached = this.ledgerCache;
    if (cached && cached.ledger === this.ledger && cached.version === version) {
      return cached;
    }
    // `getState()` already returns fresh arrays, so hand those to consumers
    // directly rather than copying every port a second time.
    const state = this.ledger.getState();
    const portFoupIds: Record<string, string[]> = {};
    for (const [portId, port] of Object.entries(state.ports)) {
      portFoupIds[portId] = port.foupIds;
    }
    const view = {
      ledger: this.ledger,
      version,
      ownership: state.ohtLoadedFoupId,
      portFoupIds,
    };
    this.ledgerCache = view;
    return view;
  }

  private resolvedGateSources(): AmhsLiveInventoryGate[] {
    if (this.resolvedGateSourcesCache) return this.resolvedGateSourcesCache;
    const byPortId = new Map<string, AmhsLiveInventoryGate>();
    for (const module of this.gateModules) {
      byPortId.set(module.loadPortId, {
        portId: module.loadPortId,
        portKind: module.role === 'entry' ? 'ENTRY_GATE' : 'EXIT_GATE',
        role: module.role,
        name: module.name,
        nodeId: module.trackNodeId,
        x: module.x,
        y: module.y,
        capacity: module.foupCapacity,
      });
    }
    for (const gate of this.gateInventory) {
      const fallback = byPortId.get(gate.portId);
      byPortId.set(gate.portId, {
        ...fallback,
        ...gate,
        nodeId: gate.nodeId || fallback?.nodeId,
        x: Number.isFinite(gate.x) ? gate.x : fallback?.x,
        y: Number.isFinite(gate.y) ? gate.y : fallback?.y,
      });
    }
    this.resolvedGateSourcesCache = [...byPortId.values()];
    return this.resolvedGateSourcesCache;
  }

  getFrame(): LiveOhtRuntimeFrame {
    return this.frame;
  }

  reset(): void {
    this.preparedTimelines.clear();
    this.orderedOhtIds = [];
    this.states.clear();
    this.ledger = new FoupOwnershipLedger();
    this.ledgerCache = null;
    this.gateInventory = [];
    this.gateModules = [];
    this.resolvedGateSourcesCache = null;
    this.railIndex = null;
    this.railTravelCache = new WeakMap();
    transferAnimationPipelineStore.reset();
    this.frame = {
      globalSimTimeS: 0,
      byOhtId: {},
      gateByPortId: {},
      portFoupIds: {},
    };
  }

  tick(globalSimTimeS: number, wallDtS: number): LiveOhtRuntimeFrame {
    const byOhtId: Record<string, LiveOhtRuntimeSnapshot> = {};
    const gateSources = this.resolvedGateSources();
    // Build the gate lock directory once per frame. The previous implementation
    // scanned every OHT state whenever a gate event became due, which degraded
    // to O(N²) when many vehicles reached gates together.
    const gateLockOwners = new Map<string, string>();
    for (const ohtId of this.orderedOhtIds) {
      const hold = this.states.get(ohtId)?.activeWallHold;
      if (hold?.gateContext && !gateLockOwners.has(hold.event.frame.portId)) {
        gateLockOwners.set(hold.event.frame.portId, ohtId);
      }
    }
    for (const ohtId of this.orderedOhtIds) {
      const prepared = this.preparedTimelines.get(ohtId);
      if (!prepared) continue;
      byOhtId[ohtId] = this.tickOht(
        ohtId,
        prepared,
        gateSources,
        gateLockOwners,
        globalSimTimeS,
        Math.max(0, wallDtS)
      );
    }
    const { ownership, portFoupIds } = this.ledgerView();
    for (const [ohtId, snapshot] of Object.entries(byOhtId)) {
      const state = this.states.get(ohtId);
      const ledgerCarry = ownership[ohtId] ?? null;
      // Keep carry when the mutable state already committed this tick but the
      // derived ledger map has not caught up (same-frame ordering).
      snapshot.carriedFoupId = ledgerCarry ?? state?.carriedFoupId ?? null;
      snapshot.carriedFoupKind = state?.carriedFoupKind ?? null;
    }
    const gateByPortId: Record<string, LiveGateRuntimeSnapshot> = {};
    for (const gate of gateSources) {
      const role = gate.role === 'entry' ? 'entry' : gate.role === 'exit' ? 'exit' : null;
      if (!role || typeof gate.x !== 'number' || typeof gate.y !== 'number') {
        continue;
      }
      gateByPortId[gate.portId] = {
        equipmentId: role === 'entry' ? 'ENTRY-EQUIPMENT' : 'EXIT-EQUIPMENT',
        role,
        portId: gate.portId,
        nodeId: gate.nodeId,
        x: gate.x,
        y: gate.y,
        status: role === 'entry' ? 'READY_FOR_PICKUP' : 'READY_FOR_DROP',
        activeEventId: null,
        activeOhtId: null,
        foupId: null,
        progress01: 0,
        phase: null,
        foupState: role === 'entry' ? 'IN_ENTRY_EQUIPMENT' : 'IN_EXIT_EQUIPMENT',
        shutterOpen01: 0,
        validationOk: true,
      };
    }
    for (const snapshot of Object.values(byOhtId)) {
      if (snapshot.gateTransfer) {
        const existing = gateByPortId[snapshot.gateTransfer.portId];
        if (
          existing?.status !== 'TRANSFERRING' ||
          snapshot.gateTransfer.status === 'TRANSFERRING'
        ) {
          gateByPortId[snapshot.gateTransfer.portId] = snapshot.gateTransfer;
        }
      }
    }
    this.frame = { globalSimTimeS, byOhtId, gateByPortId, portFoupIds };
    return this.frame;
  }

  private ensureState(ohtId: string): MutableOhtState {
    let state = this.states.get(ohtId);
    if (!state) {
      state = {
        offsetS: 0,
        lastGlobalS: null,
        lastHeadingDeg: 0,
        lastRail: null,
        carriedFoupId: null,
        carriedFoupKind: null,
        completedTransfers: new Set(),
        completedSimWaits: new Set(),
        skippedLateTransfers: new Set(),
        activeWallHold: null,
        activeSimWait: null,
        lastSnapshot: null,
        invalidGateTransfer: null,
        reportedInvalidTransfers: new Set(),
      };
      this.states.set(ohtId, state);
    }
    return state;
  }

  private tickOht(
    ohtId: string,
    prepared: PreparedOhtTimeline,
    gateSources: readonly AmhsLiveInventoryGate[],
    gateLockOwners: Map<string, string>,
    globalS: number,
    wallDtS: number
  ): LiveOhtRuntimeSnapshot {
    const state = this.ensureState(ohtId);
    const { idle, legs, playback, transfers, waits } = prepared;
    const rawDeltaS =
      state.lastGlobalS == null || globalS < state.lastGlobalS ? 0 : globalS - state.lastGlobalS;
    if (state.lastGlobalS != null && globalS < state.lastGlobalS) {
      // A seek starts a deterministic replay of local timing rather than
      // preserving wall holds from the abandoned future.
      state.offsetS = 0;
      state.completedTransfers.clear();
      state.completedSimWaits.clear();
      state.skippedLateTransfers.clear();
      state.activeWallHold = null;
      state.activeSimWait = null;
      state.carriedFoupId = null;
      state.carriedFoupKind = null;
      state.invalidGateTransfer = null;
      state.reportedInvalidTransfers.clear();
    }
    state.lastGlobalS = globalS;

    // Pickup/drop must not shift this vehicle's clock. The DES already reserved
    // those seconds on the shared timeline; a wall freeze made busy OHTs lag
    // idle ones and broke handoff order. Junction waits can still offset.
    let localS = Math.max(0, globalS - state.offsetS);

    if (state.activeSimWait) {
      if (globalS < state.activeSimWait.releaseGlobalS - EPSILON_S) {
        state.offsetS += rawDeltaS;
        localS = state.activeSimWait.anchorLocalS;
      } else {
        state.activeSimWait = null;
        localS = Math.max(0, globalS - state.offsetS);
      }
    }

    let position = getPlaybackPosition(playback, idle, localS, state.lastHeadingDeg);
    let currentNode = currentNodeAt(legs, localS);
    let moving = isMovingLeg(position.leg);
    // Record the segment's own bearing, not the eased angle: a vehicle that
    // stops near a corner would otherwise remember a heading part-way through
    // the turn and sit skewed across the rail for the whole dwell.
    if (moving && position.leg) {
      state.lastHeadingDeg = legHeadingDeg(position.leg, state.lastHeadingDeg);
      state.lastRail = this.railTravelFor(position.leg);
    }
    // Anything stationary faces along the track it arrived on.
    let headingDeg = moving ? position.angle : state.lastHeadingDeg;
    let activeWait = waitAt(waits, localS);

    if (
      !state.activeSimWait &&
      activeWait &&
      !state.completedSimWaits.has(activeWait.eventId) &&
      !activeWait.correlatedLegId &&
      moving
    ) {
      state.completedSimWaits.add(activeWait.eventId);
      state.activeSimWait = {
        event: activeWait,
        releaseGlobalS: globalS + Math.max(0, activeWait.endS - localS),
        anchorLocalS: localS,
        anchorX: position.x,
        anchorY: position.y,
        anchorAngle: state.lastHeadingDeg,
      };
    }

    const provisional: LiveOhtRuntimeSnapshot = {
      ohtId,
      globalSimTimeS: globalS,
      localSimTimeS: localS,
      scheduleOffsetS: state.offsetS,
      x: position.x,
      y: position.y,
      angle: headingDeg,
      currentNode,
      state: moving ? 'MOVING' : position.leg ? 'DWELL' : 'IDLE',
      activeLegId: position.leg?.legId ?? null,
      progress01: position.progress,
      carriedFoupId: state.carriedFoupId,
      carriedFoupKind: state.carriedFoupKind,
      transfer: null,
      gateTransfer: null,
      wait: null,
    };

    const holdAtTickStart = state.activeWallHold;
    if (!state.activeWallHold) {
      const due = transfers.find(
        (event) =>
          !state.completedTransfers.has(event.eventId) &&
          !state.skippedLateTransfers.has(event.eventId) &&
          event.startS <= localS + EPSILON_S
      );
      if (due) {
        const durationSimS = handoverDurationSimS(due, legs);
        const gateContext = resolveGateTransferContext(due.frame, gateSources);
        if (gateContext) {
          const indexedLockOwner = gateLockOwners.get(due.frame.portId);
          const lockOwner = indexedLockOwner === ohtId ? undefined : indexedLockOwner;
          const atHandoff =
            typeof gateContext.x === 'number' && typeof gateContext.y === 'number'
              ? Math.hypot(provisional.x - gateContext.x, provisional.y - gateContext.y) <=
              PRESENCE_RADIUS
              : gateContext.nodeId
                ? provisional.currentNode === gateContext.nodeId
                : false;
          const validation =
            localS >= due.endS
              ? { ok: false, reason: 'EVENT_WINDOW_EXPIRED' }
              : validateGateTransferStart({
                context: gateContext,
                frame: due.frame,
                ohtStopped: !moving,
                ohtAtHandoff: atHandoff,
                carriedFoupId: state.carriedFoupId,
                lockedByOhtId: lockOwner,
              });
          if (!validation.ok) {
            state.invalidGateTransfer = gateSnapshot(
              due,
              gateContext,
              0,
              validation,
              'INVALID',
              gateContext.x ?? provisional.x,
              gateContext.y ?? provisional.y
            );
            if (!state.reportedInvalidTransfers.has(due.eventId)) {
              state.reportedInvalidTransfers.add(due.eventId);
              console.warn('[TRANSFER_INVALID]', {
                eventId: due.eventId,
                ohtId,
                portId: due.frame.portId,
                gateRole: gateContext.role,
                reason: validation.reason,
                localSimTimeS: localS,
              });
            }
            if (localS >= due.endS) {
              state.skippedLateTransfers.add(due.eventId);
              skipPipelineTransfer(due);
            }
          } else if (durationSimS > EPSILON_S) {
            state.invalidGateTransfer = null;
            if (
              tryArmWallHold(state, due, {
                elapsedWallS: 0,
                elapsedSimS: Math.max(0, localS - due.startS),
                durationSimS,
                anchorX: position.x,
                anchorY: position.y,
                // The transfer parks the vehicle, so it keeps the bearing of the
                // segment it arrived on for the whole dwell.
                anchorAngle: state.lastHeadingDeg,
                gateContext,
                ownershipCommitted: false,
              })
            ) {
              // Later OHTs in this tick must observe the new lock, matching the
              // former ordered state scan without paying for another fleet walk.
              gateLockOwners.set(due.frame.portId, ohtId);
            }
          } else {
            state.invalidGateTransfer = null;
            this.completeTransfer(state, due);
          }
        } else if (localS >= due.endS) {
          state.skippedLateTransfers.add(due.eventId);
          skipPipelineTransfer(due);
          console.warn('[LATE_EVENT]', {
            type: due.type,
            eventId: due.eventId,
            ohtId,
            startS: due.startS,
            endS: due.endS,
            simulationTime: localS,
          });
          this.applyTransferOwnership(state, due);
        } else if (transferPresence(due, provisional)) {
          if (durationSimS > EPSILON_S) {
            tryArmWallHold(state, due, {
              elapsedWallS: 0,
              elapsedSimS: Math.max(0, localS - due.startS),
              durationSimS,
              anchorX: position.x,
              anchorY: position.y,
              // The transfer parks the vehicle, so it keeps the bearing of the
              // segment it arrived on for the whole dwell.
              anchorAngle: state.lastHeadingDeg,
              gateContext: null,
              ownershipCommitted: false,
            });
          } else if (localS >= due.endS) {
            this.completeTransfer(state, due);
          }
        }
      }
    }

    const hold = state.activeWallHold;
    if (hold) {
      hold.elapsedWallS += wallDtS;
      if (hold === holdAtTickStart) hold.elapsedSimS += rawDeltaS;
      const holdProgress = holdProgress01(hold);
      const stockerCommit = stockerOwnershipCommitProgress(hold.event);
      if (
        !hold.ownershipCommitted &&
        stockerCommit != null &&
        holdProgress + EPSILON_S >= stockerCommit
      ) {
        this.applyTransferOwnership(state, hold.event);
        hold.ownershipCommitted = true;
      } else if (
        !hold.ownershipCommitted &&
        !isStockerTransfer(hold.event) &&
        holdProgress >= gateOwnershipCommitProgress(hold.event.action)
      ) {
        // Tools and gates commit at the same physical grip/release beat their
        // renderers use (DROP 0.35, PICKUP 0.75).
        this.applyTransferOwnership(state, hold.event);
        hold.ownershipCommitted = true;
      }
      const holdComplete = hold.elapsedSimS + EPSILON_S >= hold.durationSimS;
      const stationId = transferStationId(hold.event);
      const releaseHold = () => {
        this.completeTransfer(state, hold.event, !hold.ownershipCommitted);
        state.activeWallHold = null;
        if (
          hold.gateContext &&
          gateLockOwners.get(hold.event.frame.portId) === ohtId
        ) {
          gateLockOwners.delete(hold.event.frame.portId);
        }
      };
      if (hold.awaitingCrane) {
        syncCranePhaseIfReady(stationId, hold.event.eventId, holdProgress);
        if (
          !stationId ||
          !transferAnimationPipelineStore.isAwaitingCrane(stationId, hold.event.eventId)
        ) {
          releaseHold();
        }
      } else if (holdComplete) {
        if (stationId) {
          transferAnimationPipelineStore.markOhtPhaseComplete(stationId, hold.event.eventId);
        }
        syncCranePhaseIfReady(stationId, hold.event.eventId, holdProgress);
        if (
          isStockerTransfer(hold.event) &&
          stationId &&
          transferAnimationPipelineStore.isAwaitingCrane(stationId, hold.event.eventId)
        ) {
          hold.awaitingCrane = true;
        } else {
          releaseHold();
        }
      }
    }

    activeWait = waitAt(waits, localS);
    if (
      activeWait &&
      !activeWait.correlatedLegId &&
      state.completedSimWaits.has(activeWait.eventId) &&
      !state.activeSimWait
    ) {
      activeWait = null;
    }
    const simWait = state.activeSimWait;
    const liveHold = state.activeWallHold;
    // Rendering starts only after presence validation has armed a hold. Merely
    // receiving a look-ahead transfer_event must not open shutters, move a
    // FOUP, or start the OHT hoist.
    const activeTransfer = liveHold?.event ?? null;
    const gateTransfer = liveHold?.gateContext
      ? gateSnapshot(
        liveHold.event,
        liveHold.gateContext,
        holdProgress01(liveHold),
        { ok: true },
        'TRANSFERRING',
        liveHold.anchorX,
        liveHold.anchorY
      )
      : state.invalidGateTransfer;
    let runtimeState: LiveOhtRuntimeSnapshot['state'] = moving
      ? 'MOVING'
      : position.leg
        ? 'DWELL'
        : 'IDLE';
    if (activeWait?.reason === 'nodeCapacity') runtimeState = 'BLOCKED';
    else if (activeWait) runtimeState = 'JUNCTION_WAIT';
    if (liveHold?.gateContext) {
      runtimeState = liveHold.event.action === 'PICKUP' ? 'PICKING_UP' : 'DROPPING';
    } else if (liveHold) runtimeState = 'TRANSFER';
    else if (gateTransfer && !gateTransfer.validationOk) {
      runtimeState = 'TRANSFER_WAIT';
    }

    const pose = this.railSeatedPose(
      state,
      simWait ? simWait.anchorX : position.x,
      simWait ? simWait.anchorY : position.y,
      liveHold ? liveHold.anchorAngle : simWait ? simWait.anchorAngle : headingDeg,
      !liveHold && !simWait && moving ? position.leg : null
    );

    const snapshot: LiveOhtRuntimeSnapshot = {
      ohtId,
      globalSimTimeS: globalS,
      localSimTimeS: localS,
      scheduleOffsetS: state.offsetS,
      x: pose.x,
      y: pose.y,
      angle: pose.angle,
      currentNode,
      state: runtimeState,
      activeLegId: position.leg?.legId ?? null,
      progress01: position.progress,
      carriedFoupId: state.carriedFoupId,
      carriedFoupKind: state.carriedFoupKind,
      transfer: activeTransfer
        ? {
          eventId: activeTransfer.eventId,
          action: activeTransfer.action,
          foupId: activeTransfer.frame.foupId,
          portId: activeTransfer.frame.portId,
          stationId: resolveTransferStationId(activeTransfer.frame) || undefined,
          elapsedWallS: liveHold?.elapsedWallS ?? 0,
          holdWallSec: activeTransfer.holdWallSec,
          elapsedSimS: liveHold?.elapsedSimS ?? 0,
          durationSimS: liveHold?.durationSimS ?? handoverDurationSimS(activeTransfer, legs),
          progress01: liveHold ? holdProgress01(liveHold) : 1,
          simulationActive: activeTransfer.startS <= localS && localS < activeTransfer.endS,
        }
        : null,
      gateTransfer,
      wait: activeWait
        ? {
          eventId: activeWait.eventId,
          reason: activeWait.reason,
          nodeId: activeWait.nodeId,
          blockingOhtId: activeWait.blockingOhtId,
          untilLocalSimTimeS: activeWait.endS,
        }
        : null,
    };
    state.lastSnapshot = snapshot;
    return snapshot;
  }

  private completeTransfer(
    state: MutableOhtState,
    event: OhtTransferTimelineEvent,
    applyOwnership = true
  ): void {
    state.completedTransfers.add(event.eventId);
    if (applyOwnership) this.applyTransferOwnership(state, event);
    skipPipelineTransfer(event);
  }

  private applyTransferOwnership(state: MutableOhtState, event: OhtTransferTimelineEvent): void {
    this.ledger.apply({
      sequence: event.sequence,
      eventId: event.eventId,
      operationId: event.eventId,
      kind: event.action === 'PICKUP' ? 'OHT_PICKUP' : 'OHT_DROP',
      phase: 'complete',
      intent: 'physical',
      action: event.action,
      t: event.endS,
      requestId: event.requestId,
      ohtId: event.ohtId,
      foupId: event.frame.foupId,
      portId: event.frame.portId,
      stationId: event.frame.stockerId || event.frame.toolId || undefined,
    });
    const ownership = this.ledger.getState().ohtLoadedFoupId;
    for (const [ohtId, runtime] of this.states) {
      runtime.carriedFoupId = ownership[ohtId] ?? null;
    }
    state.carriedFoupId = ownership[event.ohtId] ?? null;
    // Deliberately not cleared on the drop: a DROP hands ownership over at 0.35
    // while the vehicle still draws the carrier for the rest of the hold, and
    // resetting here would flip that carrier's colour mid-animation.
    if (event.action === 'PICKUP') {
      state.carriedFoupKind = resolvePickupFoupKind(event.frame);
    }
  }
}
